// src/components/organisms/ChatBot/ChatBotUI.tsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import '@stretto/cds-tokens/lib/css/base.css'
import './styles.scss'
import ChatMessageComponent from 'components/molecules/ChatMessage'
import InputZone from 'components/molecules/InputZone'
import { useParams } from 'react-router-dom'
import { useApolloClient, useMutation, ApolloError } from '@apollo/client'
import { useAppSelector } from 'hooks/store'

import {
  useChatHistory,
  getMessageType,
  parseCaseId,
  validateConversationIdFormat
} from './helper'

import {
  MessageType,
  MessageStatus,
  ChatMessage,
  RouteParams,
  CaseId,
  ChatBotProps,
  defaultChatBotProps
} from './types'

import { ERROR_MESSAGES, UI } from './constants'
import { logger } from './logger'
import { debugLogger } from './debugLogger'

// ---------- GraphQL ----------
import { STREAM_CHAT_MESSAGES } from 'share/subscriptions/chat'
import { SEND_MESSAGE } from 'share/mutations/chat'
import ProgressBar from 'components/molecules/ProgressBar'
import { LeftPanel, StreamEvent } from 'share/domain/chat'
import { getEmptyAuthentication } from 'reducers/authentication/authenticationSlice'

// ---------- Component ----------

const ChatBotUI: React.FC<ChatBotProps> = (props = defaultChatBotProps) => {
  const { className, onError } = { ...defaultChatBotProps, ...props }
  const activeCase = useAppSelector((state) => state.activeCase)

  // Prefer caseId from Redux activeCase; fallback to route param
  const { caseId: caseIdParam } = useParams<RouteParams>()

  const account =
    useAppSelector((state) => state.authentication.account) ??
    getEmptyAuthentication().account

  const apollo = useApolloClient()

  const [sendMessage] = useMutation(SEND_MESSAGE)

  // Conversation ID
  const conversationIdRef = useRef<string>('')

  // ---------- State ----------
  const [activeCaseIdFromStore, setActiveCaseIdFromStore] = useState(0)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [retryButtonClicked, setRetryButtonClicked] = useState(false)

  // Resolve a safe numeric case ID
  const safeCaseId: CaseId = useMemo(() => {
    if (
      typeof activeCaseIdFromStore === 'number' &&
      activeCaseIdFromStore > 0
    ) {
      return activeCaseIdFromStore
    }
    const caseId = parseCaseId(caseIdParam)
    return caseId
  }, [activeCaseIdFromStore, caseIdParam])

  // Track drag depth to handle nested drag events properly
  const dragDepthRef = useRef<number>(0)

  // Check if any message is currently being processed (thinking)
  const isMessageStreaming = useMemo(() => {
    return chatMessages.some((msg) => msg.status === MessageStatus.THINKING)
  }, [chatMessages])

  const latestMessageRef = useRef<string>('')

  // Persist the latest pillsMessages across all messages
  const [latestPillsMessages, setLatestPillsMessages] = useState<string[]>([])

  // Streaming refs
  const currentStreamingMessageIndexRef = useRef<number | null>(null)
  const messageChunkBufferRef = useRef<string>('') // currently unused; kept for future chunk coalescing
  const subscriptionRetryCountRef = useRef<number>(0)
  const subscriptionReadyRef = useRef<boolean>(false)

  // Streaming control: track if we have an active streaming message
  const activeMessageIdRef = useRef<string | null>(null)
  const [, setDisplayFeedback] = useState<boolean>(false)

  // Init control
  const initializationStateRef = useRef<{
    hasSentInitialMessage: boolean
    isInitializing: boolean
    lastCaseId: CaseId | null
  }>({
    hasSentInitialMessage: false,
    isInitializing: false,
    lastCaseId: null
  })

  const activeSubscriptionRef = useRef<ZenObservable.Subscription | null>(null)
  const activeSubConversationIdRef = useRef<string | null>(null)
  const [leftPanels, setLeftPanels] = useState<LeftPanel[]>([])

  // ---------- Utils ----------
  const clearAllTimers = useCallback(() => {
    // (no timers in this trimmed version)
  }, [])

  const isHandlerException = useCallback(
    (apolloError: ApolloError): boolean => {
      const hasGraphQlErrors = Array.isArray(apolloError?.graphQLErrors)
        ? apolloError.graphQLErrors.length > 0
        : false

      return hasGraphQlErrors
        ? apolloError.graphQLErrors.some((e) => {
            const msg = typeof e?.message === 'string' ? e.message : undefined
            return msg
              ? msg.toLowerCase().includes('exception was thrown by handler')
              : false
          })
        : false
    },
    []
  )

  const isIgnorableNoResponse = useCallback((apolloError: ApolloError) => {
    if (!apolloError) return false
    const hasGraphQlErrors = Array.isArray(apolloError.graphQLErrors)
      ? apolloError.graphQLErrors.length > 0
      : false
    if (hasGraphQlErrors) {
      return apolloError.graphQLErrors.some((e) => {
        const msg = typeof e?.message === 'string' ? e.message : ''
        return (
          msg.toLowerCase().includes('no response received') ||
          msg.toLowerCase().includes('no message returned from method')
        )
      })
    }
    const net = apolloError.networkError as unknown
    if (
      net &&
      typeof net === 'object' &&
      'message' in (net as Record<string, unknown>)
    ) {
      const msg = String((net as { message?: unknown }).message ?? '')
      if (
        msg.toLowerCase().includes('no response received') ||
        msg.toLowerCase().includes('no message returned from method')
      ) {
        return true
      }
    }
    return false
  }, [])

  const updateConversationId = useCallback(
    (newId: string) => {
      const oldId = conversationIdRef.current
      if (oldId !== newId && validateConversationIdFormat(newId)) {
        conversationIdRef.current = newId

        // Persist to localStorage per case
        const storageKey = `conversationId_${safeCaseId}`
        try {
          window.localStorage.setItem(storageKey, newId)
        } catch (error) {
          debugLogger.warn('Failed to save conversationId to localStorage', {
            error: error instanceof Error ? error.message : String(error),
            conversationId: newId,
            caseId: safeCaseId
          })
        }
      }
    },
    [safeCaseId]
  )

  // ---------- Queries ----------
  useEffect(() => {
    if (activeCase?.matter?.bccReference) {
      setActiveCaseIdFromStore(activeCase.matter.bccReference)
    }
  }, [activeCase])

  // ---------- Chat History ----------
  const {
    data: previousChatMessages,
    loading: isLoadingChatHistory,
    hasHistory: hasPreviousChatHistory
  } = useChatHistory(safeCaseId as number)

  const hasAddedSessionResumptionMessageRef = useRef<boolean>(false)
  const hasRestoredFromLocalStorageRef = useRef<boolean>(false)
  const hasLoadedHistoryForCaseRef = useRef<CaseId | null>(null)

  // ---------- Effects: case dashboard ----------

  // ---------- Effects: history -> component state ----------
  useEffect(() => {
    if (!previousChatMessages || previousChatMessages.length === 0) return

    // Skip if we've already loaded history for this case
    if (hasLoadedHistoryForCaseRef.current === safeCaseId) {
      return
    }

    // Skip if we've restored from localStorage - those messages are already in state
    if (hasRestoredFromLocalStorageRef.current) {
      hasLoadedHistoryForCaseRef.current = safeCaseId
      return
    }

    // Extract newest valid conversationId from history (UUID v4)
    const historyConversationId = previousChatMessages
      .filter((m) => typeof m.conversationId === 'string' && !!m.conversationId)
      .sort(
        (a, b) =>
          new Date(b.timestamp ?? 0).getTime() -
          new Date(a.timestamp ?? 0).getTime()
      )
      .find((m) =>
        validateConversationIdFormat(String(m.conversationId))
      )?.conversationId

    const currentConversationId = conversationIdRef.current

    if (historyConversationId) {
      // Only update if the history conversation ID is different from current
      if (historyConversationId !== currentConversationId) {
        updateConversationId(String(historyConversationId))
      }
    } else if (
      previousChatMessages.some((m) => typeof m.conversationId === 'string')
    ) {
      debugLogger.warn('Chat history contains invalid conversation IDs', {
        caseId: safeCaseId,
        currentConversationId: currentConversationId,
        invalidIds: previousChatMessages
          .filter((m) => typeof m.conversationId === 'string')
          .map((m) => String(m.conversationId))
          .join(', ')
      })
    }

    const convertedHistory: ChatMessage[] = previousChatMessages.map(
      (serverMessage) => ({
        type: getMessageType(serverMessage) as MessageType,
        message: serverMessage.content ?? '',
        messageId: serverMessage.messageId, // Preserve messageId for deduplication
        status: MessageStatus.IDLE,
        documents: serverMessage.documentList || [],
        prompts: serverMessage.pillsMessages || [],
        timestamp: serverMessage.timestamp
      })
    )

    setChatMessages((current) => {
      // If no current messages, just return the history
      if (current.length === 0) return convertedHistory

      // Create deduplication keys using messageId as primary identifier
      // This prevents duplicates when navigating back from external links
      const existingKeys = new Set(
        current.map((m) => {
          // Use messageId as primary key if available (from server)
          if (m.messageId) return m.messageId
          // Fallback to composite key for messages without messageId (new local messages)
          const key = m.timestamp
            ? `${m.type}::${m.message}::${m.timestamp}`
            : `${m.type}::${m.message}::${JSON.stringify(m).slice(0, 100)}`
          return key
        })
      )

      // Filter out history messages that already exist in current state
      const filtered = convertedHistory.filter((m) => {
        // Use messageId as primary key if available
        if (m.messageId) return !existingKeys.has(m.messageId)
        // Fallback to composite key
        const key = m.timestamp
          ? `${m.type}::${m.message}::${m.timestamp}`
          : `${m.type}::${m.message}::${JSON.stringify(m).slice(0, 100)}`
        return !existingKeys.has(key)
      })

      // If no new history messages to add, keep current state
      if (filtered.length === 0) return current

      // Merge: put history first, then current messages (preserving chronological order)
      const merged = [...filtered, ...current]

      // Sort by timestamp to maintain chronological order
      // Messages without timestamp (new ones) get current time for sorting
      return merged.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : Date.now()
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : Date.now()
        return timeA - timeB
      })
    })
    setLeftPanels(
      previousChatMessages[previousChatMessages.length - 1]?.leftPanels || []
    )

    // Set pills from the most recent history message if available
    const latestHistoryPills =
      previousChatMessages[previousChatMessages.length - 1]?.pillsMessages
    if (latestHistoryPills && latestHistoryPills.length > 0) {
      setLatestPillsMessages(latestHistoryPills)
    }

    // Mark that we've loaded history for this case
    hasLoadedHistoryForCaseRef.current = safeCaseId
  }, [previousChatMessages, updateConversationId, safeCaseId])

  // ---------- Unmount cleanup ----------
  useEffect(() => {
    return () => {
      if (activeSubscriptionRef.current) {
        activeSubscriptionRef.current.unsubscribe()
        activeSubscriptionRef.current = null
      }
      clearAllTimers()
    }
  }, [clearAllTimers])

  // ---------- Subscription ----------
  type StreamPayload = { onChatMessageReceived?: StreamEvent }

  const startStreamingSubscription = useCallback(
    (force = false) => {
      const desiredConv = conversationIdRef.current

      // Reuse only if subscription exists and is for the same conversation
      if (
        activeSubscriptionRef.current &&
        activeSubConversationIdRef.current === desiredConv &&
        !force
      ) {
        return
      }

      // Tear down existing
      if (activeSubscriptionRef.current) {
        activeSubscriptionRef.current.unsubscribe()
        activeSubscriptionRef.current = null
      }
      activeSubConversationIdRef.current = null

      messageChunkBufferRef.current = ''
      clearAllTimers()

      subscriptionReadyRef.current = false

      // Include caseId if the backend filters by it
      const sub = apollo
        .subscribe<StreamPayload>({
          query: STREAM_CHAT_MESSAGES,
          variables: { conversationId: desiredConv, caseId: Number(safeCaseId) }
        })
        .subscribe({
          next: ({ data }) => {
            subscriptionReadyRef.current = true
            // Log raw GraphQL response

            const streamEvent = data?.onChatMessageReceived
            if (!streamEvent) {
              debugLogger.warn('No onChatMessageReceived in payload', {
                rawData: data
              })
              return
            }

            if (streamEvent.leftPanels && streamEvent.leftPanels.length > 0) {
              setLeftPanels(streamEvent.leftPanels)
              window.localStorage.setItem(
                `leftPanels_${safeCaseId}`,
                JSON.stringify(streamEvent.leftPanels)
              )
            }

            // Handle pillsMessages during stream
            if (streamEvent.pillsMessages !== undefined) {
              if (streamEvent.pillsMessages.length > 0) {
                // Update with new pills
                setLatestPillsMessages(streamEvent.pillsMessages)
                // Save to localStorage for session persistence (survives navigation)
                window.localStorage.setItem(
                  `pillMessages_${safeCaseId}`,
                  JSON.stringify(streamEvent.pillsMessages)
                )
              } else {
                // Only clear pills if this is NOT a #endmessage
                const isEndMessage = streamEvent.content === '#endmessage'
                if (!isEndMessage) {
                  // Clear pills when stream returns empty array (but not for #endmessage)
                  setLatestPillsMessages([])
                  // Clear from localStorage when stream explicitly returns empty
                  window.localStorage.removeItem(`pillMessages_${safeCaseId}`)
                }
              }
            }

            const messageId = String(streamEvent.messageId || '')
            const content = streamEvent.content ?? ''

            // Adopt server-provided conversationId if no active stream yet
            if (
              streamEvent.conversationId &&
              streamEvent.conversationId !== conversationIdRef.current &&
              currentStreamingMessageIndexRef.current === null
            ) {
              updateConversationId(streamEvent.conversationId)
            }

            // Mark which conversation this sub serves
            activeSubConversationIdRef.current = conversationIdRef.current
            subscriptionRetryCountRef.current = 0

            // Guard: if backend ever sends without messageId, treat it as single atomic message
            if (!messageId) {
              const isEndWithoutId =
                typeof content === 'string' &&
                content.trim().toLowerCase() === '#endmessage'
              if (isEndWithoutId) return
              // Just append a complete bot bubble
              setChatMessages((curr) => [
                ...curr,
                {
                  type: MessageType.BOT,
                  message: content,
                  status: MessageStatus.IDLE,
                  documents: [],
                  prompts: latestPillsMessages
                }
              ])
              return
            }

            // "#endmessage" => finalize current active message (regardless of messageId)
            const isEnd =
              typeof content === 'string' &&
              content.trim().toLowerCase() === '#endmessage'
            if (isEnd) {
              // Always finalize the currently active message when we see #endmessage
              if (activeMessageIdRef.current) {
                const idx = currentStreamingMessageIndexRef.current

                setChatMessages((curr) => {
                  if (idx === null || idx < 0 || idx >= curr.length) return curr
                  const next = [...curr]
                  const msg = next[idx]
                  const finalMessage =
                    (msg.message ?? '') + (messageChunkBufferRef.current || '')

                  next[idx] = {
                    ...msg,
                    message: finalMessage,
                    status: MessageStatus.IDLE // STOP animation
                  }
                  return next
                })

                // Pills were already handled during stream - no additional action needed

                // Clear active state
                currentStreamingMessageIndexRef.current = null
                messageChunkBufferRef.current = ''
                activeMessageIdRef.current = null
                clearAllTimers()
                setDisplayFeedback(true)

                // Message completed - ready for next message
              } else {
                // No active streaming message, but check for THINKING messages
                setChatMessages((curr) => {
                  return curr.map((msg) => {
                    if (
                      msg.type === MessageType.BOT &&
                      msg.status === MessageStatus.THINKING
                    ) {
                      return { ...msg, status: MessageStatus.IDLE }
                    }
                    return msg
                  })
                })
                setDisplayFeedback(true)
              }
              return
            }

            // Non-streaming complete message (final content delivered at once)
            if (!streamEvent.isStreaming) {
              if (activeMessageIdRef.current === messageId) {
                // finalize active with final content
                const idx = currentStreamingMessageIndexRef.current
                if (idx !== null) {
                  setChatMessages((curr) => {
                    if (idx < 0 || idx >= curr.length) return curr
                    const next = [...curr]
                    next[idx] = {
                      ...next[idx],
                      message: content,
                      status: MessageStatus.IDLE
                    }
                    return next
                  })
                }
                currentStreamingMessageIndexRef.current = null
                messageChunkBufferRef.current = ''
                activeMessageIdRef.current = null
                clearAllTimers()
              } else {
                // No active streaming message, check for THINKING messages to finalize
                setChatMessages((curr) => {
                  const hasThinkingMessage = curr.some(
                    (msg) =>
                      msg.type === MessageType.BOT &&
                      msg.status === MessageStatus.THINKING
                  )

                  if (hasThinkingMessage) {
                    // Replace THINKING message with the complete message
                    const updated = curr.map((msg) => {
                      if (
                        msg.type === MessageType.BOT &&
                        msg.status === MessageStatus.THINKING
                      ) {
                        return {
                          ...msg,
                          message: content,
                          status: MessageStatus.IDLE,
                          documents: streamEvent.documentList || []
                        }
                      }
                      return msg
                    })

                    // Clear the active message ref since we've processed the response
                    activeMessageIdRef.current = null
                    currentStreamingMessageIndexRef.current = null

                    return updated
                  } else {
                    // Create a complete message immediately
                    return [
                      ...curr,
                      {
                        type: MessageType.BOT,
                        message: content,
                        status: MessageStatus.IDLE,
                        documents: streamEvent.documentList || [],
                        prompts: []
                      } as ChatMessage
                    ]
                  }
                })
              }
              return
            }

            // Handle streaming chunks: if we have an active message, append to it
            // regardless of messageId (server sends multiple IDs for one message)
            if (activeMessageIdRef.current) {
              const idx = currentStreamingMessageIndexRef.current
              if (idx === null) {
                // This shouldn't happen, but create bubble if needed
                setChatMessages((curr) => {
                  const created = [
                    ...curr,
                    {
                      type: MessageType.BOT,
                      message: content,
                      status: MessageStatus.IDLE,
                      documents: [],
                      prompts: latestPillsMessages
                    } as ChatMessage
                  ]
                  currentStreamingMessageIndexRef.current = created.length - 1
                  return created
                })
              } else {
                // Append to existing active message
                setChatMessages((curr) => {
                  if (idx >= curr.length) return curr
                  const next = [...curr]
                  const existing = next[idx]
                  next[idx] = {
                    ...existing,
                    message: (existing.message ?? '') + content,
                    status: MessageStatus.IDLE
                  }
                  return next
                })
              }
              return
            }

            // No active message? Create the first one
            activeMessageIdRef.current = messageId

            setChatMessages((curr) => {
              const created = [
                ...curr,
                {
                  type: MessageType.BOT,
                  message: content,
                  status: MessageStatus.IDLE,
                  documents: [],
                  prompts: latestPillsMessages
                } as ChatMessage
              ]
              currentStreamingMessageIndexRef.current = created.length - 1
              return created
            })
            return
          },
          error: (err) => {
            subscriptionReadyRef.current = false

            debugLogger.subscription('error', 'Subscription error occurred', {
              error: err.message,
              errorType: err.constructor.name,
              conversationId: conversationIdRef.current,
              caseId: safeCaseId,
              retryCount: subscriptionRetryCountRef.current,
              subscriptionReady: subscriptionReadyRef.current,
              hasActiveSubscription: !!activeSubscriptionRef.current,
              messageIndex: currentStreamingMessageIndexRef.current ?? undefined
            })

            logger.subscriptionError(err, {
              caseId: safeCaseId,
              messageIndex: currentStreamingMessageIndexRef.current
            })

            if (subscriptionRetryCountRef.current < UI.MAX_RETRY_ATTEMPTS) {
              subscriptionRetryCountRef.current += 1
              if (activeSubscriptionRef.current) {
                activeSubscriptionRef.current.unsubscribe()
                activeSubscriptionRef.current = null
              }
              setTimeout(() => {
                startStreamingSubscription()
              }, 1000 * subscriptionRetryCountRef.current)
              return
            }

            subscriptionRetryCountRef.current = 0
            onError?.(err)

            const idx = currentStreamingMessageIndexRef.current
            const errorMessage = err.message || ERROR_MESSAGES.STREAMING_ERROR
            if (idx !== null) {
              setChatMessages((curr) => {
                if (idx >= curr.length) return curr
                const next = [...curr]
                next[idx] = {
                  ...next[idx],
                  type: MessageType.SYSTEM,
                  status: MessageStatus.IDLE,
                  message:
                    next[idx].message && next[idx].message.length > 0
                      ? `${next[idx].message}\n\n(${errorMessage})`
                      : `${ERROR_MESSAGES.CHAT_SERVICE_UNAVAILABLE}: ${errorMessage}`
                }
                return next
              })
            } else {
              setChatMessages((curr) => [
                ...curr,
                {
                  type: MessageType.SYSTEM,
                  message: `${ERROR_MESSAGES.CHAT_SERVICE_UNAVAILABLE}: ${errorMessage}`,
                  documents: [],
                  prompts: []
                }
              ])
            }

            currentStreamingMessageIndexRef.current = null
            messageChunkBufferRef.current = ''
            if (activeSubscriptionRef.current) {
              activeSubscriptionRef.current.unsubscribe()
              activeSubscriptionRef.current = null
            }
          },
          complete: () => {
            subscriptionReadyRef.current = false
            debugLogger.subscription(
              'complete',
              'Subscription stream completed',
              {
                conversationId: conversationIdRef.current,
                caseId: safeCaseId
              }
            )
            setDisplayFeedback(true)
          }
        })
      activeSubscriptionRef.current = sub
      activeSubConversationIdRef.current = desiredConv

      // Mark “ready” after the subscribe call returns a handle
      subscriptionReadyRef.current = true
    },
    [apollo, clearAllTimers, safeCaseId, updateConversationId, onError]
  )

  // ---------- Initialization & session resumption ----------

  const sendInitialMessage = useCallback(async () => {
    const state = initializationStateRef.current
    if (state.isInitializing) return
    if (state.hasSentInitialMessage && state.lastCaseId === safeCaseId) return

    state.isInitializing = true
    state.lastCaseId = safeCaseId

    try {
      // Ensure sub is up
      if (!subscriptionReadyRef.current) {
        startStreamingSubscription()
      }

      const silentPayload = {
        conversationId: conversationIdRef.current,
        content: '',
        caseId: Number(safeCaseId),
        documentList: [] as Array<{ fileName: string; documentId: string }>
      }

      await sendMessage({ variables: { request: silentPayload } })

      state.hasSentInitialMessage = true
      state.isInitializing = false
    } catch (error) {
      state.isInitializing = false
      const apolloError = error as ApolloError

      if (isHandlerException(apolloError)) {
        return
      }

      if (isIgnorableNoResponse(apolloError)) {
        initializationStateRef.current.hasSentInitialMessage = true
        return
      }

      // Log detailed error information for debugging
      debugLogger.mutation('error', 'SendMessage failed with detailed error', {
        hasGraphQLErrors: Array.isArray(apolloError?.graphQLErrors),
        graphQLErrorCount: apolloError?.graphQLErrors?.length || 0,
        graphQLErrors:
          apolloError?.graphQLErrors
            ?.map((err) => `${err.message} (${JSON.stringify(err.extensions)})`)
            .join('; ') || 'none',
        networkError: apolloError?.networkError
          ? {
              message: apolloError.networkError.message,
              name: apolloError.networkError.name
            }
          : null,
        conversationId: conversationIdRef.current,
        caseId: safeCaseId,
        subscriptionReady: subscriptionReadyRef.current,
        hasActiveSubscription: !!activeSubscriptionRef.current,
        isHandlerException: isHandlerException(apolloError),
        isIgnorableNoResponse: isIgnorableNoResponse(apolloError)
      })

      const hasGraphQlErrors = Array.isArray(apolloError?.graphQLErrors)
        ? apolloError.graphQLErrors.length > 0
        : false

      const fallback = 'Something went wrong. Please try again.'
      setChatMessages((curr) => [
        ...curr,
        {
          type: MessageType.SYSTEM,
          message: hasGraphQlErrors
            ? fallback
            : `Failed to connect to chat service: ${
                apolloError?.message ||
                (error instanceof Error ? error.message : 'Unknown error')
              }`,
          documents: [],
          prompts: []
        }
      ])

      onError?.(error as Error)
    }
  }, [
    safeCaseId,
    onError,
    sendMessage,
    isHandlerException,
    isIgnorableNoResponse,
    startStreamingSubscription
  ])

  const handleSessionResumption = useCallback(() => {
    if (isLoadingChatHistory) return

    // Only show resume message if:
    // 1. There is previous chat history on the server
    // 2. We haven't already added the resumption message
    // 3. We haven't restored messages from localStorage (indicating this is page navigation, not true resumption)
    if (
      hasPreviousChatHistory &&
      !hasAddedSessionResumptionMessageRef.current &&
      !hasRestoredFromLocalStorageRef.current
    ) {
      const now = new Date()
      const timeString = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
      const dateString = now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })

      const resumptionMessage: ChatMessage = {
        type: MessageType.SYSTEM,
        message: `Chat has been resumed at ${timeString} on ${dateString}`,
        documents: [],
        prompts: [],
        status: MessageStatus.IDLE
      }

      setChatMessages((current) => [...current, resumptionMessage])
      hasAddedSessionResumptionMessageRef.current = true

      // After showing resume notification, send initial blank message to restart chat
      setTimeout(() => {
        sendInitialMessage()
      }, 500) // Small delay to ensure resume notification is displayed first
    } else if (hasRestoredFromLocalStorageRef.current) {
      // If we restored from localStorage, just send initial message without resume notification
      sendInitialMessage()
    }
  }, [
    isLoadingChatHistory,
    hasPreviousChatHistory,
    safeCaseId,
    sendInitialMessage
  ])

  // Primary orchestration: after history loaded, start sub, then send initial (only once)
  useEffect(() => {
    if (isLoadingChatHistory) return

    // Ensure subscription is established for current conversationId/caseId
    startStreamingSubscription()

    // Show resumed notification if we have history (this will also send initial message)
    if (hasPreviousChatHistory) {
      handleSessionResumption()
    } else {
      sendInitialMessage()
    }
  }, [
    isLoadingChatHistory,
    hasPreviousChatHistory,
    startStreamingSubscription,
    handleSessionResumption
  ])

  // Reset when case changes
  useEffect(() => {
    hasAddedSessionResumptionMessageRef.current = false
    hasRestoredFromLocalStorageRef.current = false
    hasLoadedHistoryForCaseRef.current = null
    initializationStateRef.current = {
      hasSentInitialMessage: false,
      isInitializing: false,
      lastCaseId: null
    }

    if (activeSubscriptionRef.current) {
      activeSubscriptionRef.current.unsubscribe()
      activeSubscriptionRef.current = null
    }
    activeSubConversationIdRef.current = null
    clearAllTimers()
    currentStreamingMessageIndexRef.current = null
    messageChunkBufferRef.current = ''
    subscriptionReadyRef.current = false

    // reset streaming state
    activeMessageIdRef.current = null

    // Load or generate conversationId for this case
    const storageKey = `conversationId_${safeCaseId}`
    try {
      const storedConversationId = window.localStorage.getItem(storageKey)
      if (
        storedConversationId &&
        validateConversationIdFormat(storedConversationId)
      ) {
        conversationIdRef.current = storedConversationId
      } else {
        // Generate new conversation ID and save it
        const newConversationId = uuidv4()
        conversationIdRef.current = newConversationId
        window.localStorage.setItem(storageKey, newConversationId)
      }
    } catch (error) {
      // Fallback: generate new ID without storage
      const fallbackId = uuidv4()
      conversationIdRef.current = fallbackId
      debugLogger.warn(
        'Failed to access localStorage, using fallback conversationId',
        {
          error: error instanceof Error ? error.message : String(error),
          conversationId: fallbackId,
          caseId: safeCaseId
        }
      )
    }
  }, [safeCaseId, clearAllTimers])

  // Initialize conversation ID from localStorage on mount
  useEffect(() => {
    // Only initialize if not already set
    if (conversationIdRef.current) return

    const storageKey = `conversationId_${safeCaseId}`
    try {
      const stored = window.localStorage.getItem(storageKey)
      if (stored && validateConversationIdFormat(stored)) {
        conversationIdRef.current = stored
      } else {
        // Generate new conversation ID and save it
        const newId = uuidv4()
        conversationIdRef.current = newId
        window.localStorage.setItem(storageKey, newId)
      }
    } catch (error) {
      // Fallback: generate new ID without storage
      const fallbackId = uuidv4()
      conversationIdRef.current = fallbackId
      debugLogger.warn(
        'Failed to access localStorage on mount, using fallback conversationId',
        {
          error: error instanceof Error ? error.message : String(error),
          conversationId: fallbackId,
          caseId: safeCaseId
        }
      )
    }
  }, [safeCaseId]) // Only run when safeCaseId changes or on mount

  // Clear conversation ID when tab/window closes (but not on refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const storageKey = `conversationId_${safeCaseId}`
      window.localStorage.removeItem(storageKey)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [safeCaseId])

  // Persist chat messages to localStorage when they change
  useEffect(() => {
    if (chatMessages.length > 0) {
      const storageKey = `chatMessages_${safeCaseId}`
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(chatMessages))
      } catch (error) {
        debugLogger.warn('Failed to save chat messages to localStorage', {
          error: error instanceof Error ? error.message : String(error),
          caseId: safeCaseId
        })
      }
    }
  }, [chatMessages, safeCaseId])

  // Restore chat messages from localStorage on mount
  useEffect(() => {
    const storageKey = `chatMessages_${safeCaseId}`
    const pillMessagesStorageKey = `pillMessages_${safeCaseId}`
    const leftPanelsStorageKey = `leftPanels_${safeCaseId}`
    try {
      const storedMessages = window.localStorage.getItem(storageKey)
      const storedPillMessages = window.localStorage.getItem(
        pillMessagesStorageKey
      )
      const storedLeftPanels = window.localStorage.getItem(leftPanelsStorageKey)

      // Always restore pills if they exist, regardless of whether messages exist
      if (storedPillMessages) {
        const restoredPills = JSON.parse(storedPillMessages)
        setLatestPillsMessages(restoredPills)
      }

      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages) as ChatMessage[]
        if (Array.isArray(parsedMessages) && parsedMessages.length > 0) {
          setChatMessages(parsedMessages)
          setLeftPanels(storedLeftPanels ? JSON.parse(storedLeftPanels) : [])
          hasRestoredFromLocalStorageRef.current = true
        }
      }
    } catch (error) {
      debugLogger.warn('Failed to restore chat messages from localStorage', {
        error: error instanceof Error ? error.message : String(error),
        caseId: safeCaseId
      })
    }
  }, [safeCaseId]) // Only run when case changes or on mount

  // Clear stored messages when conversation ID is cleared (on window close)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const messageStorageKey = `chatMessages_${safeCaseId}`
      const pillMessagesStorageKey = `pillMessages_${safeCaseId}`
      window.localStorage.removeItem(messageStorageKey)
      window.localStorage.removeItem(pillMessagesStorageKey)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [safeCaseId])

  // ---------- Send handler ----------
  const handleSendMessage = useCallback(
    async (
      message: string,
      documents?: Array<{ fileName: string; documentId: string }>
    ) => {
      setRetryButtonClicked(false)
      setDisplayFeedback(false)
      setLatestPillsMessages([])
      // Show user message + create THINKING bubble for bot
      setChatMessages((prev) => {
        const botIndex = prev.length + 1
        currentStreamingMessageIndexRef.current = botIndex
        // Set a temporary message ID for the THINKING message
        activeMessageIdRef.current = 'thinking-' + Date.now()
        return [
          ...prev,
          { type: MessageType.USER, message, documents: documents || [] },
          {
            type: MessageType.BOT,
            message: '',
            documents: [],
            prompts: [],
            status: MessageStatus.THINKING
          }
        ]
      })

      try {
        // Ensure sub is up for current conversation
        if (!subscriptionReadyRef.current) {
          startStreamingSubscription(true)
        }

        const requestPayload: {
          conversationId: string
          content: string
          caseId: number
          documentList: Array<{ fileName: string; documentId: string }>
        } = {
          conversationId: conversationIdRef.current,
          content: message,
          caseId: Number(safeCaseId),
          documentList: []
        }

        if (documents?.length) {
          requestPayload.documentList = documents.map((d) => ({
            fileName: d.fileName,
            documentId: d.documentId
          }))
        }

        const response = await sendMessage({
          variables: { request: requestPayload }
        })

        // If backend created or moved to a different conversation when documents are sent,
        // adopt the returned conversationId and restart the subscription so we receive stream events.
        const responseConversationId =
          response.data?.chat?.sendMessage?.conversationId
        if (
          typeof responseConversationId === 'string' &&
          responseConversationId &&
          responseConversationId !== conversationIdRef.current &&
          validateConversationIdFormat(responseConversationId)
        ) {
          updateConversationId(responseConversationId)

          // Ensure the subscription is bound to the new conversation immediately
          try {
            if (activeSubscriptionRef.current) {
              activeSubscriptionRef.current.unsubscribe()
              activeSubscriptionRef.current = null
            }
          } catch {
            // Ignore unsubscribe errors
          }
          startStreamingSubscription(true)
        }

        // Only capture documentList from mutation response when documents were actually sent
        if (documents?.length) {
          const responseDocumentList =
            response.data?.chat?.sendMessage?.documentList || []
          if (responseDocumentList.length > 0) {
            // Update the user message with the returned documentList
            setChatMessages((prev) => {
              const updated = [...prev]
              const userMessageIndex = updated.length - 2 // User message is second to last
              if (
                userMessageIndex >= 0 &&
                updated[userMessageIndex].type === MessageType.USER
              ) {
                updated[userMessageIndex] = {
                  ...updated[userMessageIndex],
                  documents: responseDocumentList
                }
              }
              return updated
            })
          }
        }
      } catch (error) {
        const apolloError = error as ApolloError

        if (isIgnorableNoResponse(apolloError)) {
          // Replace THINKING message with a SYSTEM message for ignorable errors
          setChatMessages((curr) => {
            return curr.map((msg, index) => {
              if (
                index === currentStreamingMessageIndexRef.current &&
                msg.type === MessageType.BOT &&
                msg.status === MessageStatus.THINKING
              ) {
                return {
                  type: MessageType.SYSTEM,
                  message: apolloError.message,
                  documents: [],
                  prompts: [],
                  status: MessageStatus.IDLE
                }
              }
              return msg
            })
          })

          return
        }

        const netErr = apolloError?.networkError as unknown
        const statusCode = (() => {
          if (!netErr || typeof netErr !== 'object') return undefined
          const candidate = netErr as { statusCode?: unknown; status?: unknown }
          if (typeof candidate.statusCode === 'number')
            return candidate.statusCode
          if (typeof candidate.status === 'number') return candidate.status
          return undefined
        })()
        const isGraphQLUnauth = apolloError?.graphQLErrors?.some((e) => {
          const ext = e?.extensions as unknown
          let code: string | undefined
          if (
            ext &&
            typeof ext === 'object' &&
            'code' in (ext as Record<string, unknown>)
          ) {
            const maybeCode = (ext as { code?: unknown }).code
            if (typeof maybeCode === 'string') code = maybeCode
          }
          return code === 'UNAUTHENTICATED' || code === 'FORBIDDEN'
        })
        const isAuthError =
          statusCode === 401 || statusCode === 403 || isGraphQLUnauth

        if (isAuthError)
          debugLogger.auth('Authentication required for chat functionality')

        const graphErrorMsgs = Array.isArray(apolloError?.graphQLErrors)
          ? apolloError.graphQLErrors
              .map((e) =>
                e && 'message' in e
                  ? (e as { message: string }).message
                  : undefined
              )
              .filter(Boolean)
          : []

        debugLogger.mutation('error', 'Failed to send message', {
          error:
            apolloError?.message ||
            (error instanceof Error ? error.message : 'Unknown error'),
          graphQLErrorMessages: graphErrorMsgs.join(' | '),
          statusCode
        })
        logger.error('Failed to send message', { error, message })

        const fallback = 'Something went wrong. Please try again'
        const errorMessage = isHandlerException(apolloError)
          ? fallback
          : isAuthError
          ? 'Chat service requires authentication. Please ensure you are logged in.'
          : statusCode === 500
          ? 'Chat service encountered an internal error. Please try again.'
          : `${ERROR_MESSAGES.CHAT_SERVICE_UNAVAILABLE}: ${
              apolloError?.message ||
              (error instanceof Error ? error.message : 'Unknown error')
            }`

        setChatMessages((curr) => {
          // Replace the THINKING message with a SYSTEM error message
          const updated = curr.map((msg, index) => {
            if (
              index === currentStreamingMessageIndexRef.current &&
              msg.type === MessageType.BOT &&
              msg.status === MessageStatus.THINKING
            ) {
              return {
                type: MessageType.SYSTEM,
                message: errorMessage,
                documents: [],
                prompts: [],
                status: MessageStatus.IDLE
              }
            }
            return msg
          })

          return updated
        })

        onError?.(error as Error)
      }
    },
    [
      sendMessage,
      safeCaseId,
      startStreamingSubscription,
      onError,
      isHandlerException,
      isIgnorableNoResponse
    ]
  )

  // Adapter for InputZone prop shape
  const handleSendMessageAdapter = useCallback(
    (
      message: string,
      documents?: Array<{ document_name: string; document_id: string }>
    ) => {
      latestMessageRef.current = message
      const mapped = documents?.map((d) => ({
        fileName: d.document_name,
        documentId: d.document_id
      }))
      void handleSendMessage(message, mapped)
    },
    [handleSendMessage]
  )

  // ---------- Resend message ----------
  const resendMessage = useCallback(() => {
    void handleSendMessage(latestMessageRef.current, [])
  }, [handleSendMessage])

  // Reset retry button state when latest message is a system message
  useEffect(() => {
    const latestMessage = chatMessages[chatMessages.length - 1]
    if (
      latestMessage &&
      latestMessage.type === MessageType.SYSTEM &&
      latestMessage.message.includes('Please try again')
    ) {
      setRetryButtonClicked(true)
    }
  }, [chatMessages])

  // ---------- Auto-scroll ----------
  const messageListRef = useRef<HTMLUListElement | null>(null)
  const scrollToBottomOfChat = useCallback(() => {
    // Scroll the ul element to the very bottom to show the latest bubble
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight
    }
  }, [])

  useEffect(() => {
    // Scroll to bottom when messages change (new messages added or thinking bubble replaced)
    const timer1 = setTimeout(() => {
      scrollToBottomOfChat()
    }, 10)

    // Additional scroll attempt for delayed DOM updates
    const timer2 = setTimeout(() => {
      scrollToBottomOfChat()
    }, 150)

    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
    }
  }, [chatMessages, scrollToBottomOfChat])

  // Sync CSS variables with dynamic header/menu/footer heights (optimized)
  useEffect(() => {
    let rafId: number | null = null
    let prevNav = -1
    let prevMenu = -1
    let prevFooter = -1

    const getElements = () => {
      const navbar = document.querySelector(
        '[data-testid="NavbarID"]'
      ) as HTMLElement | null
      const menu = document.querySelector(
        '.ui-dashboard-menu'
      ) as HTMLElement | null
      const footer = document.querySelector(
        '#case-level-content + section > div'
      ) as HTMLElement | null
      return { navbar, menu, footer }
    }

    const measureAndSet = () => {
      if (rafId !== null) return

      // Check if page is fully loaded
      if (document.readyState !== 'complete') {
        // If not loaded, wait for load event
        const handleLoad = () => {
          window.removeEventListener('load', handleLoad)
          measureAndSet()
        }
        window.addEventListener('load', handleLoad)
        return
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = null
        const { navbar, menu, footer } = getElements()

        // Check if all required elements are present
        if (!navbar || !menu || !footer) {
          debugLogger.warn('Required layout elements not found', {
            navbar: !!navbar,
            menu: !!menu,
            footer: !!footer,
            navbarHeight: navbar?.getBoundingClientRect().height || 0,
            menuHeight: menu?.getBoundingClientRect().height || 0,
            footerHeight: footer?.getBoundingClientRect().height || 0,
            documentReadyState: document.readyState,
            caseId: safeCaseId
          })
          return
        }

        // Use getBoundingClientRect().height to get the pixel height of the element
        const navHeight = navbar.getBoundingClientRect().height || 0
        const menuHeight = menu.getBoundingClientRect().height || 0
        const footerHeight = footer.getBoundingClientRect().height || 0

        if (
          navHeight === prevNav &&
          menuHeight === prevMenu &&
          footerHeight === prevFooter
        ) {
          return
        }

        prevNav = navHeight
        prevMenu = menuHeight
        prevFooter = footerHeight

        const root = document.documentElement
        root.style.setProperty('--menu-header', `${navHeight}px`)
        root.style.setProperty('--dashboard-menu', `${menuHeight}px`)
        root.style.setProperty('--footer-wrapper', `${footerHeight}px`)
      })
    }

    // Prefer ResizeObserver for precise updates, fallback to window events
    let ro: ResizeObserver | null = null
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(() => measureAndSet())
      const { navbar, menu, footer } = getElements()
      if (navbar) ro.observe(navbar)
      if (menu) ro.observe(menu)
      if (footer) ro.observe(footer)
    }

    window.addEventListener('resize', measureAndSet)
    window.addEventListener('orientationchange', measureAndSet)

    // Initial run
    measureAndSet()

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', measureAndSet)
      window.removeEventListener('orientationchange', measureAndSet)
      if (ro) ro.disconnect()
    }
  }, [])

  // Failsafe: Ensure messages don't get stuck in non-IDLE states
  useEffect(() => {
    const timer = setTimeout(() => {
      setChatMessages((current) => {
        let hasChanges = false
        const updated = current.map((msg, index) => {
          // Only fix bot messages that are stuck in non-IDLE states
          if (
            msg.type === MessageType.BOT &&
            msg.status !== MessageStatus.IDLE &&
            index !== currentStreamingMessageIndexRef.current
          ) {
            debugLogger.warn('Fixing stuck message state', {
              messageIndex: index,
              currentStatus: msg.status,
              messagePreview: (msg.message || '').substring(0, 30) + '...',
              wasActiveStreaming:
                index === currentStreamingMessageIndexRef.current,
              activeMessageId: activeMessageIdRef.current,
              subscriptionReady: subscriptionReadyRef.current,
              hasActiveSubscription: !!activeSubscriptionRef.current,
              caseId: safeCaseId,
              totalMessages: current.length
            })
            hasChanges = true
            return { ...msg, status: MessageStatus.IDLE }
          }
          return msg
        })
        return hasChanges ? updated : current
      })
    }, 5000) // Check after 5 seconds

    return () => clearTimeout(timer)
  }, [chatMessages]) // Run when messages change

  // ---------- Render ----------
  const memoizedMessageList = useMemo(
    () =>
      chatMessages.map((msg, index) => {
        const shouldAnimate =
          msg.type === MessageType.BOT &&
          msg.status !== undefined &&
          msg.status !== MessageStatus.IDLE

        // Use messageId as key when available for better React reconciliation
        const messageKey = msg.messageId
          ? `msg-${msg.messageId}`
          : `${index}-${msg.type}-${(msg.message || '').slice(0, 10)}`

        return (
          <ChatMessageComponent
            key={messageKey}
            type={msg.type}
            message={msg.message}
            documents={msg.documents}
            status={msg.status}
            animateTyping={shouldAnimate}
            onProgress={scrollToBottomOfChat}
            account={account}
          />
        )
      }),
    [chatMessages, scrollToBottomOfChat, account]
  )

  return (
    <div className='chatbot-container'>
      <ProgressBar leftPanels={leftPanels} />
      <section
        className={`ChatBot${isDragging ? ' dragging' : ''}${
          className ? ` ${className}` : ''
        }`}
        onDragOver={(e) => {
          e.preventDefault()
          if (dragDepthRef.current > 0) {
            setIsDragging(true)
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault()
          dragDepthRef.current++
          setIsDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          dragDepthRef.current--
          if (dragDepthRef.current <= 0) {
            dragDepthRef.current = 0
            setIsDragging(false)
          }
        }}
        onDrop={(e) => {
          e.preventDefault()
          dragDepthRef.current = 0
          setIsDragging(false)
        }}
        role='main'
        aria-label='Chat conversation interface'
        aria-live='polite'
        aria-relevant='additions'
      >
        <div
          className='message-container'
          role='log'
          aria-label='Chat messages'
          aria-live='polite'
          tabIndex={0}
          style={{ outline: 'none' }}
        >
          <ul
            ref={messageListRef}
            role='list'
            aria-label={`${chatMessages.length} messages in conversation`}
          >
            {memoizedMessageList}
            {/* 
            commenting this out for now because backend is not implemented yet
            {displayFeedback && (
              <div className='ai-feedback'>
                <p>Was this answer helpful?</p>
                <span>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    onClick={() => {
                      debugLogger.info(
                        `Clicked on thumbs up ${activeCaseIdFromStore}`
                      )
                    }}
                  >
                    <path
                      d='M18 21H7V8L14 1L15.25 2.25C15.3667 2.36667 15.4627 2.525 15.538 2.725C15.6133 2.925 15.6507 3.11667 15.65 3.3V3.65L14.55 8H21C21.5333 8 22 8.2 22.4 8.6C22.8 9 23 9.46667 23 10V12C23 12.1167 22.9833 12.2417 22.95 12.375C22.9167 12.5083 22.8833 12.6333 22.85 12.75L19.85 19.8C19.7 20.1333 19.45 20.4167 19.1 20.65C18.75 20.8833 18.3833 21 18 21ZM9 19H18L21 12V10H12L13.35 4.5L9 8.85V19ZM7 8V10H4V19H7V21H2V8H7Z'
                      fill='#667177'
                    />
                  </svg>
                </span>
                <span>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    onClick={() => {
                      debugLogger.info(
                        `Clicked on thumbs down ${activeCaseIdFromStore}`
                      )
                    }}
                  >
                    <path
                      d='M6 3H17V16L10 23L8.75 21.75C8.63333 21.6333 8.53733 21.475 8.462 21.275C8.38667 21.075 8.34933 20.8833 8.35 20.7V20.35L9.45 16H3C2.46667 16 2 15.8 1.6 15.4C1.2 15 1 14.5333 1 14V12C1 11.8833 1.01667 11.7583 1.05 11.625C1.08333 11.4917 1.11667 11.3667 1.15 11.25L4.15 4.2C4.3 3.86667 4.55 3.58333 4.9 3.35C5.25 3.11667 5.61667 3 6 3ZM15 5H6L3 12V14H12L10.65 19.5L15 15.15V5ZM17 16V14H20V5H17V3H22V16H17Z'
                      fill='#667177'
                    />
                  </svg>
                </span>
              </div>
            )} */}

            {retryButtonClicked && (
              <div className='system-message-actions'>
                <button
                  className='insufficient-data-button'
                  onClick={resendMessage}
                >
                  Retry
                </button>
              </div>
            )}
          </ul>
        </div>
        <div role='complementary' aria-label='Message input area'>
          <InputZone
            sendMessage={handleSendMessageAdapter}
            prompts={latestPillsMessages}
            isDisabled={isMessageStreaming}
            aria-label='Type your message'
          />
        </div>
      </section>
    </div>
  )
}

export default memo(ChatBotUI)
